// Logic for calculating debriefing data from game state

/**
 * Calculate final score breakdown
 */
export const calculateFinalScore = (
  customerResults,
  stats,
  budgetTotal,
  scenario
) => {
  // 1. Customer Satisfaction (40%)
  const avgCSAT = stats.averageCSAT || 0;
  const customerSatisfaction = isNaN(avgCSAT) ? 0 : (avgCSAT / 100) * 40;

  // 2. Cost Efficiency (30%)
  const budgetUsed = stats.totalCost || 0;
  const budgetPercent = budgetTotal > 0 ? (budgetUsed / budgetTotal) * 100 : 0;
  const costEfficiency = Math.max(0, 30 - (budgetPercent / 100) * 30);

  // 3. Response Time (20%)
  // Average response time vs SLA
  const slaSeconds = {
    VIP: 300, // 5 minutes
    Business: 600, // 10 minutes
    Standard: 900, // 15 minutes
  };

  const avgSLA = customerResults.length > 0
    ? customerResults.reduce((sum, r) => sum + (slaSeconds[r.customer.profile.tier] || 600), 0) / customerResults.length
    : 600; // Default to Business SLA

  const avgResponseTime = stats.averageResponseTime || 0;
  const timeRatio = avgSLA > 0 ? avgResponseTime / avgSLA : 1;
  const timeEfficiency = timeRatio <= 1 ? 100 : Math.max(0, 100 - (timeRatio - 1) * 100);
  const responseTime = (timeEfficiency / 100) * 20;

  // 4. Protocol Adherence (10%)
  const totalViolations = stats.totalViolations || 0;
  const maxViolations = customerResults.length > 0 ? customerResults.length * 2 : 1; // Assume max 2 violations per customer
  const violationRate = maxViolations > 0 ? totalViolations / maxViolations : 0;
  const protocolAdherence = Math.max(0, 10 - violationRate * 10);

  const totalScore = customerSatisfaction + costEfficiency + responseTime + protocolAdherence;

  // Determine grade
  let grade;
  if (totalScore >= 90) grade = 'A+';
  else if (totalScore >= 80) grade = 'A';
  else if (totalScore >= 70) grade = 'B';
  else if (totalScore >= 60) grade = 'C';
  else grade = 'D';

  return {
    total_score: Math.round(totalScore),
    breakdown: {
      customer_satisfaction: Math.round(customerSatisfaction * 10) / 10,
      cost_efficiency: Math.round(costEfficiency * 10) / 10,
      response_time: Math.round(responseTime * 10) / 10,
      protocol_adherence: Math.round(protocolAdherence * 10) / 10,
    },
    grade,
  };
};

/**
 * Generate recommendations based on performance
 */
export const generateRecommendations = (customerResults, stats, finalScore) => {
  const recommendations = [];

  // Check prioritization - only recommend if Protocol Adherence is not 100%
  // Protocol Adherence of 100% means no violations were detected
  const protocolAdherence = finalScore.breakdown.protocol_adherence || 0;
  const hasPrioritizationViolations = protocolAdherence < 10;

  if (hasPrioritizationViolations) {
    const vipResults = customerResults.filter((r) => r.customer.profile.tier === 'VIP');
    const avgVIPResponse = vipResults.length > 0
      ? vipResults.reduce((sum, r) => sum + r.responseTime, 0) / vipResults.length
      : 0;
    const avgResponse = stats.averageResponseTime;

    if (avgVIPResponse > avgResponse * 1.2) {
      recommendations.push({
        category: 'prioritization',
        severity: 'critical',
        title: 'Prioritize VIP Customers',
        description:
          'VIP customers were served slower than average. Always prioritize VIP customers first, especially those with critical timing.',
        example: 'Sarah Chen (VIP) was served after 2 other customers, despite having a critical board meeting.',
      });
    }
  }

  // Check communication timing
  const delayedCount = customerResults.filter((r) => r.timing === 'delayed').length;
  if (delayedCount > customerResults.length * 0.3) {
    recommendations.push({
      category: 'speed',
      severity: 'important',
      title: 'Improve Response Speed',
      description:
        'More than 30% of customers were contacted after 15 minutes. Aim to contact customers within 5 minutes for proactive rating.',
      example: 'Try to select and contact customers within 5 minutes of crisis start.',
    });
  }

  // Check budget usage
  if (finalScore.breakdown.cost_efficiency < 20) {
    recommendations.push({
      category: 'budgeting',
      severity: 'important',
      title: 'Optimize Budget Usage',
      description:
        'Budget was used inefficiently. Consider more cost-effective options while maintaining CSAT.',
      example: 'For Standard tier customers, economy class transport may be sufficient.',
    });
  }

  // Check communication channel appropriateness
  const inappropriateComm = customerResults.filter((r) => {
    const comm = r.choices.communication;
    if (!comm) return false;
    const tier = r.customer.profile.tier;
    return (
      (tier === 'VIP' && comm.channel === 'email') ||
      (tier === 'VIP' && comm.channel === 'sms' && comm.tone === 'minimal')
    );
  });

  if (inappropriateComm.length > 0) {
    recommendations.push({
      category: 'communication',
      severity: 'critical',
      title: 'Improve Communication Channel Selection',
      description:
        'VIP customers received inappropriate communication channels. VIP customers expect phone calls with empathetic tone.',
      example: 'Sarah Chen (VIP) received SMS instead of a phone call.',
    });
  }

  return recommendations;
};

/**
 * Generate skill gaps
 */
export const generateSkillGaps = (customerResults, stats, finalScore) => {
  const gaps = [];

  // Prioritization gap
  const prioritizationScore = finalScore.breakdown.customer_satisfaction >= 35 ? 90 : 60;
  if (prioritizationScore < 80) {
    gaps.push({
      category: 'prioritization',
      severity: prioritizationScore < 60 ? 'critical' : 'important',
      title: 'Prioritization',
      description:
        'Customer prioritization needs improvement. Always serve VIP customers first, especially those with critical timing.',
      score: prioritizationScore,
      example: 'VIP customers should be handled before Standard tier customers.',
    });
  }

  // Response speed gap
  const speedScore = finalScore.breakdown.response_time >= 18 ? 90 : 75;
  if (speedScore < 85) {
    gaps.push({
      category: 'speed',
      severity: 'important',
      title: 'Response Speed',
      description:
        `Average response time was ${Math.round(stats.averageResponseTime / 60)} minutes. Aim for under 5 minutes for proactive rating.`,
      score: speedScore,
    });
  }

  // Budgeting gap
  const budgetScore = finalScore.breakdown.cost_efficiency >= 25 ? 90 : 70;
  if (budgetScore < 80) {
    gaps.push({
      category: 'budgeting',
      severity: 'nice-to-have',
      title: 'Budget Management',
      description: 'Budget usage could be more efficient while maintaining customer satisfaction.',
      score: budgetScore,
    });
  }

  return gaps;
};

/**
 * Generate timeline events
 */
export const generateTimelineEvents = (customerResults, scenario) => {
  const events = [];

  // Customer events
  customerResults.forEach((result) => {
    if (result.timing_data) {
      events.push({
        time: result.timing_data.contact_time,
        label: result.customer.profile.name,
        type: 'customer',
        timing: result.timing,
        delay: result.timing_data.delay_minutes,
      });
    }
  });

  // Sort by time (handles both "HH:MM" and "Monday, HH:MM" formats)
  events.sort((a, b) => {
    // Extract time from format "Monday, 10:00" or just "10:00"
    const extractTime = (timeStr) => {
      const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
      if (!timeMatch) return 0;
      const hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      
      // Extract day if present (Monday=0, Tuesday=1, etc.)
      const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      let day = 0;
      for (let i = 0; i < weekdays.length; i++) {
        if (timeStr.includes(weekdays[i])) {
          day = i;
          break;
        }
      }
      
      return day * 24 * 60 + hours * 60 + minutes;
    };
    
    return extractTime(a.time) - extractTime(b.time);
  });

  // Add crisis start only if no customer was handled at that exact time
  // Format crisis time with day (always Day 1 = Monday)
  const crisisStartTime = scenario.crisis?.trigger?.announced_at || scenario.time?.start || '08:00';
  const crisisTime = `Monday, ${crisisStartTime}`;
  const hasCustomerAtCrisisTime = events.some(e => {
    // Check if any event time matches crisis time (allowing for format differences)
    const eventTimeMatch = e.time.match(/(\d{1,2}):(\d{2})/);
    const crisisTimeMatch = crisisTime.match(/(\d{1,2}):(\d{2})/);
    return eventTimeMatch && crisisTimeMatch && 
           eventTimeMatch[1] === crisisTimeMatch[1] && 
           eventTimeMatch[2] === crisisTimeMatch[2] &&
           e.time.includes('Monday');
  });
  
  if (!hasCustomerAtCrisisTime) {
    events.unshift({
      time: crisisTime,
      label: 'Crisis Start',
      type: 'crisis',
    });
  }

  return events;
};
